// dataAdapter.js — normalizes the rich JSON dataset (v1.0) into the
// backward-compatible flat shape the GCBC UI components already consume.
//
// Why: the canonical JSON ships with nested objects (address.{city,state},
// bankAccount.{routingNumber,accountLast4}, kyc.{status}) and updated id
// formats (brand_home_depot vs brn_homedepot, status enums with no spaces).
// The 34 existing pages were built against the flat schema. This module
// reconciles the two without forcing a 30-file UI refactor.

// ─── Brand accent palette (synthesized — old data had hardcoded accents) ────
const BRAND_ACCENTS = {
  brand_home_depot:   "#F96302",
  brand_lowes:        "#004990",
  brand_best_buy:     "#0046BE",
  brand_target:       "#CC0000",
  brand_walmart:      "#0071CE",
  brand_amazon:       "#FF9900",
  brand_starbucks:    "#006241",
  brand_apple:        "#111111",
  brand_nike:         "#111111",
  brand_olive_garden: "#7BA428",
  brand_steam:        "#1B2838",
  brand_playstation:  "#003791",
  brand_sephora:      "#000000",
  brand_old_navy:     "#004B87",
  brand_cheesecake:   "#7B3F00",
  brand_macys:        "#E21A2C",
};
const DEFAULT_ACCENT = "#0F766E";

// ─── Status enum harmonization ──────────────────────────────────────────────
// New JSON uses spaceless camel-style strings; old UI expects same except
// PaymentRun.Generated vs Sent. We pass through (no remap needed) — the
// existing badge components already understand both vocabularies.

// ─── Admin role: "SuperAdmin" → "Super Admin" (UI labels) ───────────────────
const ROLE_LABEL = {
  SuperAdmin:        "Super Admin",
  DealManager:       "Deal Manager",
  PaymentsOverseer:  "Payments Overseer",
  ReadOnly:          "Read-only Operator",
};

// ─── Default validation rule for old IDs not present in new data ────────────
// Old IDs (rul_001..rul_018) appear inside legacy deal.validationRuleIds. Map
// them to nearest new rule equivalents where possible.
const LEGACY_RULE_MAP = {
  rul_001: "rule_card_length_16",
  rul_002: "rule_pin_4_digit",
};

// ────────────────────────────────────────────────────────────────────────────
// Brand normalization
// ────────────────────────────────────────────────────────────────────────────
export function normalizeBrand(b) {
  return {
    id:                       b.id,
    name:                     b.name,
    slug:                     b.slug,
    category:                 b.category,
    aliases:                  b.aliases || [],
    defaultRate:              b.defaultRate,
    defaultValidationRuleIds: b.defaultValidationRuleIds || [],
    accent:                   BRAND_ACCENTS[b.id] || DEFAULT_ACCENT,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Deal normalization
// ────────────────────────────────────────────────────────────────────────────
// Multi-brand "umbrella" deals: source of truth is now `brandConfigs[]` on each
// deal record in deals.json. The adapter normalizes that into both:
//   - a `brandConfigs` array (per-brand economics — used by new code), and
//   - legacy aggregate fields (rate, capTotal, capUsed, min/maxFace, perMemberCap,
//     brandId, brandIds) for any unmigrated page that still reads them.
const LEGACY_MULTI_BRANDS = {
  deal_001: ["brand_home_depot", "brand_lowes"],
  deal_004: ["brand_steam", "brand_playstation"],
  deal_006: ["brand_walmart", "brand_target"],
};

// Build a default brandConfig from legacy deal fields when JSON doesn't carry one.
function legacyToBrandConfig(d, brandId, isPrimary) {
  return {
    brandId,
    isPrimary,
    rate:         d.rate,
    capTotal:     d.capTotal,
    capUsed:      d.capUsed,
    minFace:      d.faceValueMin ?? d.minFace,
    maxFace:      d.faceValueMax ?? d.maxFace,
    perMemberCap: d.perMemberCap ?? Math.round((d.capTotal || 0) * 0.25),
  };
}

export function normalizeDeal(d) {
  const primary = d.brandId;
  // Source brandConfigs from JSON if present; otherwise synthesize from legacy fields.
  let brandConfigs = Array.isArray(d.brandConfigs) && d.brandConfigs.length
    ? d.brandConfigs.map(cfg => ({
        brandId:      cfg.brandId,
        isPrimary:    !!cfg.isPrimary,
        rate:         Number(cfg.rate) || 0,
        capTotal:     Number(cfg.capTotal) || 0,
        capUsed:      Number(cfg.capUsed) || 0,
        minFace:      Number(cfg.minFace) || 0,
        maxFace:      Number(cfg.maxFace) || 0,
        perMemberCap: Number(cfg.perMemberCap) || 0,
      }))
    : null;
  if (!brandConfigs) {
    const ids = Array.isArray(d.brandIds) && d.brandIds.length
      ? d.brandIds
      : (LEGACY_MULTI_BRANDS[d.id] || [primary]);
    brandConfigs = Array.from(new Set([primary, ...ids])).map(bid =>
      legacyToBrandConfig(d, bid, bid === primary)
    );
  }
  // Guarantee exactly one primary; default to first if none flagged.
  if (!brandConfigs.some(c => c.isPrimary)) brandConfigs[0].isPrimary = true;

  const brandIds   = brandConfigs.map(c => c.brandId);
  const primaryCfg = brandConfigs.find(c => c.isPrimary) || brandConfigs[0];
  // Aggregate / derived legacy fields.
  const capTotal   = brandConfigs.reduce((a, c) => a + c.capTotal, 0);
  const capUsed    = brandConfigs.reduce((a, c) => a + c.capUsed, 0);
  const minFace    = Math.min(...brandConfigs.map(c => c.minFace));
  const maxFace    = Math.max(...brandConfigs.map(c => c.maxFace));
  // ratesUniform: helper for UIs to decide between "92%" and "92–94%" / "varies"
  const ratesUniform = brandConfigs.every(c => c.rate === primaryCfg.rate);
  const rate   = primaryCfg.rate;
  const margin = typeof d.margin === "number" ? d.margin : Math.max(0, +(100 - rate).toFixed(2));

  return {
    id:                  d.id,
    name:                d.name,
    brandId:             primaryCfg.brandId,         // legacy/primary
    brandIds,                                        // all accepted brands
    isMultiBrand:        brandIds.length > 1,
    brandConfigs,                                    // ← NEW: per-brand economics
    ratesUniform,                                    // true ⇒ show one rate; false ⇒ "varies"
    status:              d.status,
    rate,                                            // primary rate (back-compat)
    margin,                                          // platform spread (synthetic)
    minFace,                                         // aggregate
    maxFace,                                         // aggregate
    capTotal,                                        // aggregate
    capUsed,                                         // aggregate
    perMemberCap:        primaryCfg.perMemberCap,    // primary's (back-compat)
    openedAt:            d.openAt ?? d.openedAt,
    deadline:            d.deadline,
    paymentTerms:        d.paymentTerms,
    validationRuleIds:   (d.validationRuleIds || []).map(r => LEGACY_RULE_MAP[r] || r),
    publicNote:          d.publicNote || "",
    whyBuying:           d.whyBuying || "",
    faqOverride:         Array.isArray(d.faqOverride) ? d.faqOverride : null,
    isFeatured:          !!d.isFeatured,
    internalNote:        d.internalNotes ?? d.internalNote ?? "",
    createdBy:           d.createdById ?? d.createdBy,
    createdAt:           d.createdAt,
    publishedAt:         d.publishedAt,
    closedAt:            d.closedAt,
    closedReason:        d.closedReason,
    reservationsCount:   d.reservationsCount ?? 0,
    cardsSubmittedCount: d.cardsSubmittedCount ?? 0,
    cardsApprovedCount:  d.cardsApprovedCount ?? 0,
  };
}

// Helper: get the brandConfig for a given deal+brand. Falls back to primary.
export function getBrandConfig(deal, brandId) {
  if (!deal || !Array.isArray(deal.brandConfigs)) return null;
  return deal.brandConfigs.find(c => c.brandId === brandId)
      || deal.brandConfigs.find(c => c.isPrimary)
      || deal.brandConfigs[0]
      || null;
}

// ────────────────────────────────────────────────────────────────────────────
// Member normalization — flatten address + bankAccount
// ────────────────────────────────────────────────────────────────────────────
export function normalizeMember(m) {
  const addr = m.address || {};
  const bank = m.bankAccount || {};
  return {
    id:                m.id,
    applicationId:     m.applicationId,
    firstName:         m.firstName,
    lastName:          m.lastName,
    email:             m.email,
    phone:             m.phone,
    dateOfBirth:       m.dateOfBirth,
    ssnLast4:          m.ssnLast4,
    // flat address (back-compat)
    street:            addr.street,
    city:              addr.city,
    state:             addr.state,
    zip:               addr.zip,
    address:           addr, // also expose nested form
    // flat bank (back-compat)
    bankName:          bank.holderName ? `${bank.accountType === "savings" ? "Savings" : "Checking"} • ${bank.holderName}` : null,
    bankHolderName:    bank.holderName,
    routingNumber:     bank.routingNumber,
    accountLast4:      bank.accountLast4,
    accountType:       bank.accountType,
    bankAccount:       bank,
    bankCapturedAt:    bank.capturedAt,
    // status + auth
    status:            m.status,
    mfaEnabled:        m.mfaEnabled ?? false,
    signInMethod:      m.signInMethod,
    preferredContact:  "email",
    memberSince:       m.memberSince,
    lastSignIn:        m.lastSignInAt ?? m.lastSignIn,
    lifetimeEarnings:  m.lifetimeEarnings ?? 0,
    pendingBalance:    m.pendingBalance ?? 0,
    termsAcceptedAt:   m.termsAcceptedAt,
    // suspension / deactivation
    suspendedAt:       m.suspendedAt,
    suspendReason:     m.suspendReason,
    deactivatedAt:     m.deactivatedAt,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Application normalization
// ────────────────────────────────────────────────────────────────────────────
export function normalizeApplication(a) {
  const addr = a.address || {};
  // Map onboarding-stage status → derived kycStatus for the old UI badge.
  let kycStatus = "NotStarted";
  if (a.kyc?.status === "Pass") kycStatus = "Passed";
  else if (a.kyc?.status === "Fail") kycStatus = "Failed";
  else if (a.kyc?.status === "InProgress") kycStatus = "InProgress";
  else if (a.status === "ApprovedAwaitingKYC") kycStatus = "NotStarted";

  const reviewNotes = a.reviewerNotes
    ? [{ at: a.reviewedAt || a.submittedAt, actor: a.reviewerId, text: a.reviewerNotes }]
    : [];

  return {
    id:                a.id,
    firstName:         a.firstName,
    lastName:          a.lastName,
    email:             a.email,
    phone:             a.phone,
    dateOfBirth:       a.dateOfBirth,
    ssnLast4:          a.ssnLast4,
    street:            addr.street,
    city:              addr.city,
    state:             addr.state,
    zip:               addr.zip,
    address:           addr,
    appliedAt:         a.submittedAt ?? a.appliedAt,
    submittedAt:       a.submittedAt,
    reviewedAt:        a.reviewedAt,
    status:            a.status,
    kycStatus,
    kyc:               a.kyc,
    referralSource:    a.referralSource || "",
    monthlyVolumeUSD:  a.monthlyVolumeUSD ?? null,
    sourceOfCards:     a.sourceOfCards,
    termsAccepted:     a.termsAccepted ?? false,
    reviewerId:        a.reviewerId || null,
    reviewerNotes:     a.reviewerNotes,
    reviewNotes,
    rejectReason:      a.rejectReason || null,
    resultingMemberId: a.resultingMemberId || null,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Reservation normalization
// ────────────────────────────────────────────────────────────────────────────
export function normalizeReservation(r) {
  return {
    id:                  r.id,
    memberId:            r.memberId,
    dealId:              r.dealId,
    brandId:             r.brandId || null,                 // ← NEW: per-brand on multi-brand deals
    faceValueReserved:   r.faceValueReserved,
    expectedPayout:      r.expectedPayout,
    status:              r.status,
    cardsSubmitted:      r.cardsSubmittedCount ?? r.cardsSubmitted ?? 0,
    cardsApproved:       r.cardsApprovedCount ?? r.cardsApproved ?? 0,
    faceValueSubmitted:  r.faceValueSubmitted ?? 0,
    faceValueApproved:   r.faceValueApproved ?? 0,
    createdAt:           r.reservedAt ?? r.createdAt,
    reservedAt:          r.reservedAt,
    deadline:            r.deadline,
    cancelledAt:         r.cancelledAt,
    cancelReason:        r.cancelReason,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Submission normalization
// ────────────────────────────────────────────────────────────────────────────
export function normalizeSubmission(s, dealsById) {
  // brandId is implicit via deal — denormalize for UI convenience.
  const brandId = s.brandId ?? dealsById[s.dealId]?.brandId ?? null;
  return {
    id:                 s.id,
    reservationId:      s.reservationId,
    memberId:           s.memberId,
    dealId:             s.dealId,
    brandId,
    submittedAt:        s.submittedAt,
    reviewedAt:         s.reviewedAt,
    status:             s.status,
    cardCount:          s.cardCount,
    totalFaceValue:     s.totalFaceValue,
    approvedFaceValue:  s.approvedFaceValue ?? 0,
    rawText:            s.rawText || "",
    memberNote:         s.memberNote || null,
    flagReason:         s.flagReason || null,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Card normalization
// ────────────────────────────────────────────────────────────────────────────
export function normalizeCard(c, subsById) {
  const sub = subsById[c.submissionId] || {};
  // Synthesize a display cardNumber from last4 so the UI's masking logic works.
  const last4 = c.cardNumberLast4 || "0000";
  const cardNumber = `••••••••••••${last4}`;
  return {
    id:               c.id,
    submissionId:     c.submissionId,
    reservationId:    c.reservationId ?? sub.reservationId,
    memberId:         c.memberId ?? sub.memberId,
    brandId:          c.brandId,
    cardNumber,
    cardNumberLast4:  last4,
    pinPresent:       c.pinPresent ?? true,
    faceValue:        c.faceValue,
    status:           c.status,
    approvedAt:       c.approvedAt,
    rejectedAt:       c.rejectedAt,
    flagReason:       c.flagReason || null,
    parseConfidence:  c.parseConfidence ?? 0.95,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// PaymentItem normalization
// ────────────────────────────────────────────────────────────────────────────
export function normalizePaymentItem(p, runsByItem, cardsById) {
  const runId = p.paymentRunId ?? runsByItem[p.id] ?? null;
  // Best-effort dealId derived from first card → submission → deal (handled at consume time).
  return {
    id:             p.id,
    memberId:       p.memberId,
    runId,
    submissionId:   p.submissionId ?? null,
    dealId:         p.dealId ?? null,
    amount:         p.amount,
    cardCount:      p.cardCount,
    cardIds:        p.cardIds || [],
    status:         p.status,
    scheduledFor:   p.scheduledAt ?? p.scheduledFor,
    sentAt:         p.sentAt,
    completedAt:    p.completedAt,
    refNumber:      p.achTraceNumber ?? p.refNumber ?? null,
    bankLast4:      p.bankAccountLast4 ?? p.bankLast4,
    returnReason:   p.returnReason || null,
    returnedAt:     p.returnedAt,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// PaymentRun normalization
// ────────────────────────────────────────────────────────────────────────────
export function normalizePaymentRun(r) {
  return {
    id:               r.id,
    generatedAt:      r.generatedAt,
    generatedBy:      r.generatedById ?? r.generatedBy,
    cutoffDate:       r.cutoffDate,
    memberCount:      r.memberCount,
    itemCount:        (r.itemIds || []).length,
    itemIds:          r.itemIds || [],
    totalAmount:      r.totalAmount,
    status:           r.status,
    exportedAt:       r.exportedAt,
    exportedBy:       r.exportedById,
    exportFormat:     r.exportFormat,
    csvDownloads:     r.exportFormat === "CSV"   ? 1 : 0,
    nachaDownloads:   r.exportFormat === "NACHA" ? 1 : 0,
    notes:            r.notes || null,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Audit event normalization
// ────────────────────────────────────────────────────────────────────────────
export function normalizeAuditEvent(e) {
  // Build a human description if not provided.
  const description = e.description
    || (e.entityLabel ? `${humanizeAction(e.action)} — ${e.entityLabel}` : humanizeAction(e.action));
  // For System events the JSON omits actorId — coerce to "system" so all
  // string ops (startsWith etc.) in the UI stay safe.
  const actorId = e.actorId
    || (e.actorType === "System" ? "system" : "system");
  return {
    id:           e.id,
    at:           e.timestamp ?? e.at,
    actorId,
    actorName:    e.actorName,
    actorRole:    e.actorRole,
    action:       e.action,
    entityType:   e.entityType,
    entityId:     e.entityId,
    entityLabel:  e.entityLabel,
    description,
    ipAddress:    e.ipAddress || (e.ipAddressLast4 ? `***.***.***.${e.ipAddressLast4}` : "system"),
    isDecryption: e.action?.toLowerCase().includes("decrypt") || !!e.isDecryption,
    details:      e.details || null,
  };
}

function humanizeAction(action) {
  if (!action) return "";
  return action
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, c => c.toUpperCase())
    .trim();
}

/**
 * parseRateChange — detects retroactive rate-change notes in a deal's
 * `internalNote` (e.g. "Rate bumped 89→91 on May 5; existing reservations
 * honour new rate (retroactive)").
 *
 * Returns { from, to, retroactive } or null if no rate-change phrase found.
 */
export function parseRateChange(note) {
  if (!note) return null;
  // Matches: "89→91", "89→ 91", "89%→91%", "89 -> 91"
  const m = note.match(/(\d{1,3})\s*(?:%\s*)?(?:→|->|to)\s*(\d{1,3})\s*%?/);
  if (!m) return null;
  const from = Number(m[1]);
  const to = Number(m[2]);
  if (!Number.isFinite(from) || !Number.isFinite(to) || from === to) return null;
  return {
    from,
    to,
    retroactive: /retroactive|existing\s+reservations\s+honour|honor/i.test(note),
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Admin user normalization — convert role enum to display label
// ────────────────────────────────────────────────────────────────────────────
export function normalizeAdminUser(a) {
  return {
    id:           a.id,
    firstName:    a.firstName,
    lastName:     a.lastName,
    email:        a.email,
    role:         ROLE_LABEL[a.role] || a.role,
    roleCode:     a.role,
    mfaEnabled:   a.mfaEnabled,
    status:       a.status,
    lastSignIn:   a.lastSignInAt,
    createdAt:    a.createdAt,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Validation rule passthrough
// ────────────────────────────────────────────────────────────────────────────
export function normalizeValidationRule(r) {
  return {
    id:          r.id,
    name:        r.name,
    appliesTo:   r.appliesTo,
    type:        r.ruleType,
    expression:  r.expression,
    severity:    r.severity,
    description: r.description,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// One-shot normalization entry point used by store.js
// ────────────────────────────────────────────────────────────────────────────
export function normalizeAll(raw) {
  const brands         = raw.brands.map(normalizeBrand);
  const deals          = raw.deals.map(normalizeDeal);
  const dealsById      = Object.fromEntries(deals.map(d => [d.id, d]));
  const members        = raw.members.map(normalizeMember);
  const applications   = raw.applications.map(normalizeApplication);
  const reservations   = raw.reservations.map(normalizeReservation);
  const submissions    = raw.submissions.map(s => normalizeSubmission(s, dealsById));
  const subsById       = Object.fromEntries(submissions.map(s => [s.id, s]));
  const cards          = raw.cards.map(c => normalizeCard(c, subsById));

  // Build runsByItem index so payment items can attach their runId.
  const runsByItem = {};
  raw.paymentRuns.forEach(run => {
    (run.itemIds || []).forEach(itemId => { runsByItem[itemId] = run.id; });
  });
  const cardsById      = Object.fromEntries(cards.map(c => [c.id, c]));
  const paymentItems   = raw.paymentItems.map(p => normalizePaymentItem(p, runsByItem, cardsById));
  // Backfill dealId on payment items by tracing through cardIds → card → submission → deal.
  paymentItems.forEach(p => {
    if (p.dealId) return;
    const firstCardId = p.cardIds[0];
    if (!firstCardId) return;
    const card = cardsById[firstCardId];
    if (!card) return;
    const sub = subsById[card.submissionId];
    if (sub) {
      p.dealId = sub.dealId;
      if (!p.submissionId) p.submissionId = sub.id;
    }
  });

  const paymentRuns    = raw.paymentRuns.map(normalizePaymentRun);
  const auditEvents    = raw.auditEvents.map(normalizeAuditEvent);
  const adminUsers     = raw.adminUsers.map(normalizeAdminUser);
  const validationRulesNorm = raw.validationRules.map(normalizeValidationRule);

  // ─── Recompute reservation aggregates from actual card rows ────────────
  // Seed JSON often drifts (declared cardsSubmitted/cardsApproved don't match
  // the real cards). Source of truth = cards table. Re-derive so UI is always
  // consistent. We also recompute on the submission side.
  const cardsByReservation = {};
  cards.forEach(c => {
    if (!c.reservationId) return;
    (cardsByReservation[c.reservationId] ||= []).push(c);
  });
  reservations.forEach(r => {
    const rc = cardsByReservation[r.id] || [];
    r.cardsSubmitted     = rc.length;
    r.cardsApproved      = rc.filter(c => c.status === "Approved").length;
    r.faceValueSubmitted = rc.reduce((sum, c) => sum + (c.faceValue || 0), 0);
    r.faceValueApproved  = rc
      .filter(c => c.status === "Approved")
      .reduce((sum, c) => sum + (c.faceValue || 0), 0);
  });

  const cardsBySubmission = {};
  cards.forEach(c => {
    if (!c.submissionId) return;
    (cardsBySubmission[c.submissionId] ||= []).push(c);
  });
  submissions.forEach(s => {
    const sc = cardsBySubmission[s.id] || [];
    s.cardCount         = sc.length;
    s.totalFaceValue    = sc.reduce((sum, c) => sum + (c.faceValue || 0), 0);
    s.approvedFaceValue = sc
      .filter(c => c.status === "Approved")
      .reduce((sum, c) => sum + (c.faceValue || 0), 0);
  });

  // ─── Enrich payment items with dealId + brandId resolved via their cards ─
  // Seed payment-items.json doesn't carry dealId/brandId. Resolve once from
  // the cards table (→ reservation → deal/brand) so UI always has them.
  // Fallback: if no cardIds are set, infer by matching member's approved cards
  // with the payment amount @ deal rate so demo payment-history rows always
  // show a deal/brand instead of "—".
  const cardById = Object.fromEntries(cards.map(c => [c.id, c]));
  const reservationById = Object.fromEntries(reservations.map(r => [r.id, r]));
  const cardsByMember = {};
  cards.forEach(c => {
    if (c.status !== "Approved" || !c.memberId) return;
    (cardsByMember[c.memberId] ||= []).push(c);
  });
  const dealById = Object.fromEntries(deals.map(d => [d.id, d]));
  paymentItems.forEach(p => {
    if (p.dealId && p.brandId) return;
    // Primary: resolve via first explicit cardId.
    const firstCardId = (p.cardIds || [])[0];
    let card = firstCardId ? cardById[firstCardId] : null;
    // Fallback 1: this payment has empty cardIds. Look for one of this
    // member's approved cards whose face × deal rate ≈ payment amount.
    if (!card && p.memberId) {
      const memberCards = cardsByMember[p.memberId] || [];
      card = memberCards.find(c => {
        const res = reservationById[c.reservationId];
        const d   = dealById[res?.dealId];
        const cfg = (d?.brandConfigs || []).find(bc => bc.brandId === res?.brandId) || d?.brandConfigs?.[0];
        const rate = cfg?.rate ?? d?.rate ?? 0;
        if (!rate) return false;
        const payout = Math.round(c.faceValue * rate) / 100;
        return Math.abs(payout - p.amount) < 5;
      });
      // Fallback 2 (loose match): for aggregate payments that don't map to a
      // single card, attribute to the member's most-recently approved card so
      // the demo always surfaces a meaningful deal/brand. Production would
      // store dealId/brandId explicitly on the payment record.
      if (!card && memberCards.length > 0) {
        const sorted = [...memberCards].sort((a, b) =>
          new Date(b.approvedAt || b.submittedAt || 0) - new Date(a.approvedAt || a.submittedAt || 0));
        card = sorted[0];
      }
    }
    const reservation = card?.reservationId ? reservationById[card.reservationId] : null;
    if (!p.dealId)  p.dealId  = reservation?.dealId  || card?.dealId  || p.dealId;
    if (!p.brandId) p.brandId = reservation?.brandId || card?.brandId || p.brandId;
  });

  return {
    brands,
    deals,
    members,
    applications,
    reservations,
    submissions,
    cards,
    paymentItems,
    paymentRuns,
    auditEvents,
    validationRules: validationRulesNorm,
    adminUsers,
    manifest: raw.manifest,
  };
}
